import type { FC } from 'react'

import { Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList } from '@skytech/utils'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { useProfile } from '@/hooks/user'
import type { User } from '@/modules/admin/resources'
import { UserListItem } from '@/modules/admin/users/UserListItem'

import { ExportToExcel } from '../components/ExportToExcel'

export const UserList: FC<{
  users: User[]
  searchFilter: string
  onEdit: (id: number) => void
}> = ({ users, searchFilter, onEdit }) => {
  const { locale } = useAppContext()
  const profile = useProfile()
  const translate = useTranslation()

  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof parsedItemsFiltered)[number]>()

  const stringMatcher = (item: User) =>
    Object.values({ ...item, ...item.userGroup })
      .join('')
      .toLowerCase()
      .includes(searchFilter.replace(/\s+/g, '').toLowerCase())
  const itemsFiltered = users?.filter(stringMatcher)
  const parsedItemsFiltered = itemsFiltered.map(item => ({
    ...item,
    name: `${item.firstName} ${item.lastName}`,
    userGroupDesc: item.userGroup?.description,
  }))
  const sortedList = getSortedList({ list: parsedItemsFiltered, querySort, querySortBy, locale })
  const excelExportData = itemsFiltered.map(
    ({ username, firstName, lastName, email, phoneNumber, userGroup, session }) => ({
      username,
      email,
      firstName,
      lastName,
      phoneNumber: phoneNumber || '',
      'user group': userGroup?.description,
      'last login': session?.lastRead ? new Date(session.lastRead) : null,
      currency: profile?.currency,
    })
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  return (
    <>
      <ExportToExcel
        data={excelExportData}
        filename={translate('Users')}
        options={{
          columnFormats: {
            'last login': 'date',
          },
        }}
      />
      {itemsFiltered.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th id="name" onClick={handleSort}>
                <Translation id="name" />
              </Th>
              <Th id="userGroupDesc" onClick={handleSort}>
                <Translation id="user group" />
              </Th>
              <Th id="email" className="hidden md:table-cell" onClick={handleSort}>
                <Translation id="email" />
              </Th>
              <Th id="phoneNumber" className="hidden md:table-cell" onClick={handleSort}>
                <Translation id="phone number" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map(({ id, ...rest }) => (
              <UserListItem
                key={id}
                onEdit={() => onEdit(id)}
                searchFilter={searchFilter}
                {...rest}
              />
            ))}
          </tbody>
        </Table>
      ) : (
        <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
